import { z } from 'zod';

import { listQuerySchema } from './common.js';
import {
  notificationChannelSchema,
  type ConnectionStatus,
  type NotificationChannel,
} from './enums.js';
import { channelProviderConfigSchema } from './notification-channels.js';

/**
 * NOTIFICATIONS — in-app bildirim, kanal tercihleri, e-posta.
 *
 * Tasarım §4.2 (bildirim aksiyonu), §9 (hata bildirimi).
 *
 * ---------------------------------------------------------------------------
 * 🔴 Bildirim metni hangi dilde saklanır
 * ---------------------------------------------------------------------------
 * `Notification.title/body` düz metin kolonları. İki dilli bir üründe
 * kaydedilen metin, sonradan dil değiştiren kullanıcıya YANLIŞ DİLDE görünür
 * ve geçmiş bildirimler kalıcı olarak öyle kalır.
 *
 * Sözleşme kararı:
 *   - `type` + `data.params` SAKLANIR — asıl gerçek bunlar.
 *   - `title`/`body` kolonlarına İNGİLİZCE YEDEK metin yazılır (e-posta ve
 *     olası dışa aktarma için bir şey lazım).
 *   - Arayüz `type` + `params` ile i18n anahtarından çevirir.
 * Bu karar alınmazsa ürün fiilen tek dilli olur.
 *
 * ---------------------------------------------------------------------------
 * 🔴 SMTP kapalı — bu davranış KORUNACAK
 * ---------------------------------------------------------------------------
 * `MailService.send` bugün başarısızlıkta FIRLATMIYOR; loglayıp `false`
 * dönüyor. Bu bilinçli bir karar: davet, rapor ya da uyarı akışı posta
 * gitmedi diye çökmemeli. Bildirim satırı yine yazılır, `sentAt` boş kalır ve
 * arayüz "e-posta gönderilemedi" işareti gösterebilir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Tip kataloğu — TEK KAYNAK
// ═══════════════════════════════════════════════════════════════════════════

export const NOTIFICATION_TYPES = [
  // otomasyon
  'automation.approval_pending',
  'automation.action_applied',
  'automation.action_failed',
  'automation.run_skipped',
  /**
   * Faz 3 — "kural sana haber vermek istedi".
   *
   * `notify` / `notify_external` aksiyonunun ÜRETTİĞİ bildirim. Faz 3'e kadar
   * `automation.action_applied` tipini EZİYORDU: iki farklı olay tek tipe
   * yazılıyordu ve sonuçları somuttu —
   *
   *   • Kullanıcı "otomasyon bir değişiklik uyguladı" bildirimlerini kapatınca
   *     "kural seni uyardı" bildirimleri de susuyordu. İkisi aynı şey değil:
   *     biri yapılmış bir işin kaydı, diğeri kullanıcının kurduğu uyarının ta
   *     KENDİSİ. Uyarıyı kapatmak için kuralı silmek gerekirdi.
   *   • `/admin/notifications` sayacı iki olayı topluyordu.
   *   • Metin `titleKey`/`bodyKey` ile eziliyordu; ezme unutulsa
   *     "Automation applied a change" yazacaktı — hiçbir şey uygulanmadığı hâlde.
   */
  'automation.rule_notified',
  // bağlantı
  'connection.disconnected',
  'connection.degraded',
  'connection.recovered',
  'sync.failed',
  // kreatif
  'creative.ready',
  'creative.failed',
  'creative.refunded',
  /**
   * Faz 3 — "stüdyoda seni bekleyen bir iyileştirme görevi var".
   *
   * `creative_improve` aksiyonunun açtığı DRAFT görev. Bu da
   * `automation.action_applied`ı eziyordu ve orada bir de BOZUK GÖVDE vardı:
   * o tipin metni `{ruleName}` / `{targetName}` bekliyor, çağıran ise
   * `params: { creativeType }` gönderiyordu. Yer tutucular boş doldurulup
   * ekranda `" updated."` gibi bir cümle çıkıyordu.
   *
   * Kendi tipi olunca metin de kendi parametreleriyle yazılabiliyor.
   *
   * 🔴 `creative.ready` DEĞİL: o, üretimi BİTMİŞ bir kreatifi anlatıyor ve
   * kullanıcı ona tıklayınca hazır görseli bekliyor. Burada henüz hiçbir şey
   * üretilmedi — kredi bile harcanmadı, görev stüdyoda taslak olarak duruyor.
   */
  'creative.task_opened',
  /**
   * Şema borç turu — "bir kreatif senin onayını bekliyor".
   *
   * 🔴 `automation.approval_pending` YENİDEN KULLANILMADI. O tipin metni
   * *"«{ruleName}» kuralı {targetName} üzerinde bir değişiklik önerdi"*
   * diyor; kreatif onayında `ruleName` gizli sistem kuralının adı
   * (`__creative__`) olurdu ve kullanıcı kurmadığı bir kuralın adını
   * e-postada görürdü. `creative.task_opened`ın Faz 3'te düzelttiği hatanın
   * birebir aynısı — tipi ezmek, metni bozar.
   */
  'creative.approval_pending',
  /// Kreatif onaylandı ya da reddedildi — ÜRETİCİYE gider (`audience: actor`).
  /// Onaycıya değil: kararı zaten o verdi.
  'creative.approval_decided',
  /**
   * ÜRÜN GÖRSELİ ANALİZİ BİTTİ (21 Ağu 2026).
   *
   * 🔴 `creative.ready` YENİDEN KULLANILMADI ve gerekçe `creative.task_opened`
   * ile birebir aynı: o tip ÜRETİLMİŞ bir kreatifi anlatıyor, kullanıcı ona
   * tıklayınca hazır bir görsel/video bekliyor. Burada üretilmiş bir şey yok —
   * kullanıcının stüdyoya dönüp üç konseptten birini SEÇMESİ gerekiyor ve
   * asıl üretim ondan sonra başlıyor. Tipi ezmek, metni bozar.
   *
   * Bildirimin var olma sebebi kredi: analiz worker'da koşuyor ve kullanıcı
   * sekmeyi kapatsa da sürüyor. Bittiğini söyleyen bir şey olmasa, ödenmiş 2
   * kredinin karşılığı kullanıcı stüdyoya kendiliğinden dönene kadar
   * beklerdi.
   */
  'creative.vision_ready',
  /// Analiz terminal hatayla kapandı ve KREDİ İADE EDİLDİ. Ayrı tip: "hazır"
  /// bildiriminin gövdesi konsept sayısını yazıyor, burada konsept yok.
  'creative.vision_failed',
  /**
   * PRODÜKSİYON — reklam videosu motoru (Kreatif stüdyo Faz 2, 1 Eki 2026).
   *
   * 🔴 `creative.*` tipleri YENİDEN KULLANILMADI — `creative.vision_ready`
   * ile aynı gerekçe: o tipler TEK bir render'ı anlatıyor. Prodüksiyon
   * dakikalar-saatler süren ve kullanıcıda birkaç kez DURAN bir akış; her
   * durak kendi cümlesini istiyor ve kullanıcı sekmeyi kapatmış olabilir —
   * haber vermeyen bir kapı, ödenmiş storyboard kredisini bekletir.
   *
   *   `question`          bir kapı açıldı (soru, konsept seçimi, senaryo
   *                       düzenleme, başarısız çekim, revizyon rızası)
   *   `storyboard_ready`  storyboard hazır, üretim onayı bekleniyor
   *   `delivered`         teslim edildi — sürümler kütüphanede
   *   `failed`            terminal hata; o aşamanın rezervi TAMAMEN iade
   *
   * Dördü de dedupe anahtarlı (`productionNotifyDedupeKey`): aşama işi
   * yeniden denenirse aynı kapı için ikinci bildirim gitmez.
   */
  'production.question',
  'production.storyboard_ready',
  'production.delivered',
  'production.failed',
  /**
   * GÖRSEL KAMPANYA SETİ (`ProductionKind.IMAGE_SET`, Dalga 5b, 2 Eki 2026) —
   * aynı dört durak, aynı motor ve aynı sayfa, AYRI tip. Metin değil tip
   * ayrıldı, çünkü: tek tip + nötr metin İngilizce/Türkçe'de çalışırdı ama
   * betikle üretilen 11 dilin kitabı ("Ihr Werbevideo ist fertig") DeepL
   * kapalıyken güncellenemiyor ve set için yine "video" derdi; yeni tip o
   * dillerde DOĞRU İngilizce metne düşer. Seçim worker'da
   * (`production-notify.ts` → `productionNotifyType`). Gövdede sayı yok:
   * banner setinde tek boyut/tek anahtar görsel "1 görseller" olurdu ve
   * sunucu metni ICU çoğul taşımıyor.
   */
  'production.set_question',
  'production.set_storyboard_ready',
  'production.set_delivered',
  'production.set_failed',
  /**
   * SAĞLAYICI BEKLEMESİ (3 Eki 2026) — BİZİM sağlayıcı hesabımız (fal bakiyesi,
   * anahtar) kullanılamadığı için prodüksiyon düşmek yerine bekliyor
   * (`PRODUCTION_PROVIDER_HOLD_MAX_DAYS`, `production.ts`).
   *
   * `provider_wait` — başlatana (`actor`) TEK sakin haber: bir şey yapmasına
   * gerek yok, bekleme için ücret yok, kendiliğinden sürecek. Video/set ayrımı
   * YOK: metin "üretim" diyor, ikisine de doğru. Metin kullanıcının bakiyesini
   * suçlamaz — eksik olan bizim kapasitemiz.
   *
   * `provider_blocked` — PLATFORM YÖNETİCİLERİNE (`specific_user`, kişinin en
   * eski üyeliğine — `support.ticket_assigned` / backstage köprüsüyle aynı
   * çözüm ve aynı bedel): "sağlayıcıya bakiye yükle, N prodüksiyon bekliyor".
   * Kiracı verisi taşımıyor (yalnız sağlayıcı adı); kritik — kapatılabilseydi
   * bekleyen filmleri kimse fark etmezdi.
   */
  'production.provider_wait',
  'production.provider_blocked',
  // faturalama
  'billing.credit_low',
  'billing.credit_exhausted',
  'billing.limit_warning',
  'billing.grace_started',
  'billing.grace_ended',
  'billing.payment_failed',
  /// Deneme bitişine az kaldı. Ayrı tip: `limit_warning` HARCAMA sınırını
  /// anlatıyor, bu ise SÜRENİN dolduğunu — ikisi aynı anda da olabilir.
  'billing.trial_ending',
  /// Deneme bitti, abonelik EXPIRED. Kullanıcı plan seçene kadar otomasyon,
  /// kreatif üretimi ve ajanın yazma araçları duruyor.
  'billing.trial_ended',
  // ekip
  'team.invitation_sent',
  'team.member_joined',
  'team.role_changed',
  // veri
  'data.export_ready',

  // ═════════════════════════════════════════════════════════════════════════
  // KAMPANYA YAŞAM DÖNGÜSÜ — 16 Eyl 2026
  // ═════════════════════════════════════════════════════════════════════════
  /**
   * 🔴 Neden `automation.action_applied` YETMİYOR.
   *
   * O tip bir KURALIN yaptığı işi anlatıyor ve metni de öyle:
   * *"«{ruleName}» {targetName} güncelledi"*. Burada bir kural yok — bir
   * insan panelden kampanyayı duraklattı ya da ajan onaylı bir öneriyi
   * uyguladı. Kural adı yerine boş dize basardı (bu dosyada üç kez yaşandı,
   * `creative.task_opened`ın gerekçesine bak) ve kullanıcı kendi yaptığı
   * işlemi "otomasyon değiştirdi" diye görürdü.
   *
   * Ayrıca kitle farklı: bir kampanyanın duraklatılması EKİBİN TAMAMINI
   * ilgilendiriyor (yayın durdu), otomasyon kaydı ise yöneticileri.
   */
  'campaign.created',
  /// Duraklatıldı / yeniden başlatıldı / arşivlendi — `status` parametresi
  /// hangisi olduğunu söylüyor. Üç ayrı tip AÇILMADI: kullanıcı tercih
  /// ekranında "kampanya durumu değişti" diye tek satır görmek istiyor,
  /// "duraklatıldı"yı açıp "başlatıldı"yı kapatmak anlamlı bir istek değil.
  'campaign.status_changed',
  'campaign.budget_changed',
  /**
   * Platform yayına almayı REDDETTİ (`CAMPAIGN_EMPTY`, bid_strategy eksik,
   * ödeme yöntemi yok…). `critical` — kullanıcı kampanyayı açtığını sanıp
   * günlerce sıfır harcamayla bekliyor; ürünün sessizce çalışmadığı tam
   * olarak bu durum.
   */
  'campaign.launch_failed',
  /**
   * Faz 8 D (30 Eyl 2026) — platform bir REKLAMI reddetti.
   *
   * Yayıncı worker gözcüsü (`operator/operator-watch.service.ts` →
   * `scanDisapproved`): senkron reklamı `DISAPPROVED` işaretleyince (beş
   * okuyucu) çalışma alanının sahip/yöneticilerine. Reklam başına TEK kez
   * (Redis işareti 7 gün); gözcünün bir çalışma alanını ilk görüşünde
   * mevcut retler TABAN sayılıyor — dağıtımda geçmiş retler yağmur olmasın.
   * `campaign.launch_failed`dan ayrı: o, kampanyanın YAYINA ALINAMADIĞINI
   * söylüyor (anlık, kullanıcı ekranda); bu, yayındaki bir reklamın inceleme
   * sonrası düştüğünü (sonradan, kimse bakmıyor). Hedef kampanya sayfası;
   * nedeni ve düzeltme/itiraz sohbetteki `policy_issue` kartında.
   */
  'ad.disapproved',

  // ═════════════════════════════════════════════════════════════════════════
  // AJAN (AI) — 16 Eyl 2026
  // ═════════════════════════════════════════════════════════════════════════
  /**
   * Onaylanan ajan önerisi UYGULANDI.
   *
   * Bu tip olmadan ajanın uyguladığı her değişiklik `automation.*` tipiyle
   * gidiyordu ve gövdede kuralın adı olarak gizli sistem workflow'unun adı —
   * `__agent__` — görünüyordu. Kullanıcının hiç kurmadığı, adını hiç
   * duymadığı bir "kural" e-postasına düşüyordu.
   */
  /**
   * Ajan bir değişiklik ÖNERDİ, onay bekliyor.
   *
   * 🔴 `automation.approval_pending` YENİDEN KULLANILMADI — bu dosyada
   * dördüncü kez aynı gerekçe. O tipin gövdesi *"«{ruleName}» kuralı
   * {targetName} için onayınızı bekliyor"*; ajan önerisinde `ruleName`
   * gizli sistem workflow'unun adı (`__agent__`) ya da boş dize olurdu.
   * Ölçüldü: çağıran `params: { tool, target }` gönderiyordu ve metin
   * `{ruleName}` / `{targetName}` beklediği için kullanıcıya giden cümle
   * *"«» kuralı  için onayınızı bekliyor"* idi.
   */
  'agent.approval_pending',
  /**
   * YÖNETİCİ KARARI BEKLEYEN KART — 30 Eyl 2026 (Faz 8 dalga 2).
   *
   * 🔴 `agent.approval_pending` YENİDEN KULLANILMADI: o tipin kitlesi `actor`
   * (kartı isteyen kişi) ve metni "onayını bekliyor" diyor. Burada kartı bir
   * ÜYE istedi ama kararı yalnız yönetici verebiliyor (geri alınamaz, hassas,
   * kişisel veri, erişim — `requiredDecisionRole`); üyeye "onayını bekliyor"
   * demek yalan, yöneticinin haberi de olmuyordu. Kitle OWNER + ADMIN, kart
   * başına bir bildirim; bağlantı onay kuyruğuna (`workflow_action`).
   */
  'agent.approval_needs_admin',
  'agent.optimization_applied',
  'agent.optimization_failed',
  /**
   * OTONOM OPERATÖR — 29 Eyl 2026 (OTONOM-OPERATOR-PLANI.md §1.C).
   *
   * 🔴 `agent.approval_pending` YENİDEN KULLANILMADI: o tipin kitlesi
   * `actor` — öneriyi sohbette İSTEYEN kişi. Operatör koşusunun arkasında bir
   * kişi yok; öneriyi arka plan AI'sı kendi başına hazırladı ve kararı
   * çalışma alanının yöneticileri veriyor. Aynı tip kullanılsaydı bildirim
   * kimseye gitmezdi (aktör yok) — ya da kitle kuralı tipe göre değil
   * çağırana göre değişirdi.
   *
   * Üçü de koşu başına TEK bildirim (sayı + özet), öneri başına değil:
   * bir devriye beş öneri çıkarırsa zil beş satırla dolmasın.
   */
  /// Operatörün öneri(leri) otomatik pilot politikasından geçmedi, onay bekliyor.
  'agent.operator_action_required',
  /// Operatör otomatik pilot politikası içinde değişiklik UYGULADI.
  'agent.operator_applied',
  /// Operatör önemli bir bulgu paylaştı (aksiyon olmadan da).
  'agent.operator_insight',

  // ═════════════════════════════════════════════════════════════════════════
  // PERFORMANS — 16 Eyl 2026
  // ═════════════════════════════════════════════════════════════════════════
  /**
   * 🔴 Bunlar OTOMASYON KURALI DEĞİL.
   *
   * Kullanıcı bir eşik yazmadan, hiçbir şey kurmadan alıyor: hesap her gün
   * taranıyor (`apps/worker/src/performance/`) ve dünkü değerler önceki
   * yedi günün ortalamasıyla karşılaştırılıyor. Otomasyon kuralı isteyen
   * kullanıcı zaten kendi eşiğini yazabiliyor; bu ise ürünün varsayılan
   * olarak söylediği şey.
   *
   * İyi haber de bildiriliyor (`conversions_up`): bildirim altyapısı yalnız
   * kötü haber taşırsa kullanıcı zil simgesini korku kaynağı olarak öğrenir
   * ve bakmayı bırakır.
   */
  'performance.conversions_up',
  'performance.conversions_down',
  /// Dönüşüm başına maliyet fırladı — harcama aynı kalsa bile verim düştü.
  'performance.cpa_spike',
  /// Harcama sıçraması: bütçe değişmeden gerçekleşen harcama artışı genelde
  /// bir kuralın ya da platform optimizasyonunun yan etkisi.
  'performance.spend_spike',
  'performance.roas_drop',

  // ═════════════════════════════════════════════════════════════════════════
  // HESAP VE GÜVENLİK — 16 Eyl 2026
  // ═════════════════════════════════════════════════════════════════════════
  /**
   * 🔴 10 Eki 2026: `account.password_changed` ve
   * `account.password_reset_requested` KALDIRILDI — parola yok, giriş
   * e-postaya giden kodla. Hesabın kurtarma yolu artık e-posta adresinin
   * kendisi; onun değişimi `account.email_changed`.
   */
  'account.email_changed',
  /// Tanınmayan bir cihaz/tarayıcıdan giriş. Eşik: aynı kullanıcının daha
  /// önce hiç görülmemiş `userAgent` parmak izi (`sessions` tablosu).
  'account.login_new_device',

  // ═════════════════════════════════════════════════════════════════════════
  // HESAP GÜVENLİĞİ — ince olay turu, 16 Eyl 2026
  // ═════════════════════════════════════════════════════════════════════════
  /**
   * Dokuzunun da tek bir işlevi var: kullanıcıya *"bunu ben yapmadım"*
   * dedirtmek. Bu yüzden hepsi `critical` (kapatılamaz) ve hepsi KİŞİYE
   * gidiyor — yöneticiye değil. Bir yöneticinin, ekip arkadaşının giriş
   * denemelerini görmesi güvenlik değil gözetim olurdu.
   *
   * 🔴 `login_failed` EŞİKLİ. Her başarısız denemede bildirim üretmek iki
   * şeyi birden bozardı: kullanıcının zilini (kodu iki kez yanlış yazan
   * herkes bildirim alırdı) ve güvenliği — adresi bilen biri, art arda yanlış
   * kod yazarak istediği kişiye istediği kadar SMS göndertebilirdi.
   */
  'account.login_failed',
  'account.email_change_requested',
  'account.session_revoked',
  'account.sessions_revoked',
  'account.two_factor_enrolled',
  'account.two_factor_reset',
  'account.admin_login_blocked',
  'account.admin_access_changed',

  // ── EKİP ─────────────────────────────────────────────────────────────────
  /**
   * `team.member_joined` ve `role_changed` zaten vardı; eksik olan AYRILMA
   * tarafıydı. Bir üyenin çıkarılması, davetin iptali ve sahiplik devri
   * kayda geçiyordu ama kimseye HABER VERİLMİYORDU — oysa ekip listesine
   * bakmayan bir yönetici bunu haftalarca fark etmez.
   */
  'team.invitation_resent',
  'team.invitation_revoked',
  'team.member_removed',
  'team.member_left',
  'team.ownership_transferred',

  // ── ÇALIŞMA ALANI ────────────────────────────────────────────────────────
  /**
   * 🔴 `workspace.deleted` YOK ve olamaz: bildirim satırının kendisi o
   * çalışma alanına ait (`Notification.workspaceId`, `onDelete: Cascade`).
   * Silinen alana bildirim yazmak, aynı işlemde silinecek bir satır üretmek
   * olurdu. Silme kaydı denetim defterinde duruyor.
   */
  'workspace.created',
  'workspace.updated',

  // ── BAĞLANTILAR ──────────────────────────────────────────────────────────
  /**
   * Mevcut üçü (`disconnected`/`degraded`/`recovered`) SAĞLIK olaylarıydı:
   * bağlantı kendiliğinden bozulduğunda çıkıyorlardı. Buradaki beşi ise
   * İNSAN EYLEMİ: biri platformu bağladı, biri kaldırdı, biri mağazayı
   * kesti. İkisini aynı tipe toplamak, "hesabın koptu" uyarısını kullanıcının
   * kendi yaptığı bir kaldırma işleminde de göndermek olurdu.
   */
  'connection.created',
  'connection.removed',
  'connection.account_connected',
  'connection.store_connected',
  'connection.store_disconnected',

  // ── OTOMASYON: KURAL YAŞAM DÖNGÜSÜ ───────────────────────────────────────
  /**
   * Kuralın ÇALIŞMASI bildiriliyordu (`action_applied`), KURULMASI değil.
   * Oysa para hareket ettiren bir kuralın açılması/kapatılması/silinmesi,
   * tek bir koşusundan daha önemli: kapatılmış bir kural sessizce hiçbir şey
   * yapmaz ve bunu kimse fark etmez.
   */
  'automation.rule_created',
  'automation.rule_enabled',
  'automation.rule_disabled',
  'automation.rule_deleted',
  'automation.action_rejected',
  'automation.action_rolled_back',

  // ── FATURALAMA: PARA HAREKETLERİ ─────────────────────────────────────────
  /**
   * Mevcut faturalama tipleri hep KÖTÜ haberdi (kredi bitti, ödeme düştü,
   * deneme doldu). Ödemenin ALINDIĞI, kredinin yüklendiği ve faturanın
   * kesildiği anlar hiç bildirilmiyordu — yani kullanıcı parasının
   * karşılığını aldığını yalnız kötü haberlerden öğreniyordu.
   */
  'billing.plan_changed',
  'billing.topup_purchased',
  'billing.payment_recorded',
  'billing.payment_refunded',
  'billing.payment_disputed',
  'billing.invoice_issued',

  // ── FATURALAMA: HATIRLATMA ZİNCİRİ (1 Eki 2026) ──────────────────────────
  /**
   * Ürün sahibi: "plan bitince hiçbir yerde uyarı yok — hatırlatma gitsin,
   * adama yapışalım". Takvim `billing-alert.ts` → `reminderFor` (worker'ın
   * saatlik bakım turu, alanın yerel 09:00–20:00 penceresi, alan + gün
   * başına bir). Abonelik başladığı an zincir kendiliğinden duruyor: takvim
   * her turda GÜNCEL durumdan hesaplanıyor.
   *
   *   `subscription_reminder` — plan BİTTİ (EXPIRED/CANCELED): +1, +3, +7,
   *       +14, +30, sonra ayda bir (en fazla üç ay). `{date}` bitiş günü.
   *   `payment_overdue` — yenileme ödemesi alınamıyor (PAST_DUE), abonelik
   *       yeniden denemeler tükenince bitecek: T-3, T-1, T-0. `{until}`.
   *   `grace_ending` — harcama sınırı aşıldı, ek süre bitiyor: T-3, T-1, T-0.
   *
   * (Denemenin T-3/T-1/T-0'ı mevcut `billing.trial_ending`; ek süre bittikten
   * sonraki zincir mevcut `billing.grace_ended`.)
   */
  'billing.subscription_reminder',
  'billing.payment_overdue',
  'billing.grace_ending',
  /**
   * "Sahibinden iste" — ödeme yetkisi olmayan üye (ADMIN/MEMBER/VIEWER)
   * şeritten tek tıkla sahibe hatırlatıyor. Alan başına GÜNDE BİR
   * (`POST /billing/alert/ask-owner`). Yalnız OWNER'a gider.
   */
  'billing.renewal_requested',

  // ── KREATİF ──────────────────────────────────────────────────────────────
  'creative.published',

  // ── DESTEK ───────────────────────────────────────────────────────────────
  /**
   * `audience: 'actor'` — talebi açan kişi. Yanıt geldiğinde ona haber
   * vermeyen bir destek sistemi, kullanıcıyı sayfayı yenilemeye mecbur
   * bırakır; çoğu kullanıcı yenilemez ve talep ölü görünür.
   */
  'support.ticket_replied',
  'support.ticket_closed',
  /**
   * 17 Eyl 2026 — dört tip birden eklendi ve ikisi PERSONELE gidiyor.
   *
   * `ticket_created` kullanıcıya bir MAKBUZ: talebi açtıktan sonra hiçbir
   * şey almayan kullanıcı, talebin kaydedilip kaydedilmediğini bilmiyor ve
   * ikinci bir talep açıyor (kuyruğu iki katına çıkaran davranış tam olarak
   * bu). `ticket_resolved` de aynı boşluğu kapatıyor: personel "çözüldü"
   * diyor ve kullanıcı bunu ancak panele girerse görüyordu.
   *
   * `ticket_assigned` ve `ticket_sla_breached` ise PLATFORM PERSONELİNE
   * gidiyor — `audience: 'specific_user'` ve hedef kişinin KENDİ en eski
   * üyeliğine yazılıyor (`notification-backstage.bridge.ts` ile aynı çözüm
   * ve aynı bedel: hiçbir çalışma alanına üye olmayan personel bunları
   * alamıyor). Gerekçesi orada; tekrarlanmadı.
   */
  'support.ticket_created',
  'support.ticket_resolved',
  'support.ticket_assigned',
  'support.ticket_sla_breached',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const NOTIFICATION_GROUPS = [
  'automation',
  'connection',
  'creative',
  'billing',
  'team',
  'data',
  /// 16 Eyl 2026 — dördü birden eklendi. Grup, tercih ekranının ve zil
  /// menüsünün SEKME birimidir: bir tip yanlış gruba konursa kullanıcı onu
  /// kapatmak istediğinde alakasız üç bildirimi de kapatır.
  'campaign',
  'agent',
  'performance',
  'account',
  /// 16 Eyl 2026 — ince olay turu. `workspace` ayarlar/sahiplik, `support`
  /// destek talepleri. `team`e katlanmadılar: kullanıcı "ekip bildirimlerini
  /// kapat" dediğinde çalışma alanının ayar değişikliklerini de kapatırdı.
  'workspace',
  'support',
] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export interface NotificationTypeDefinition {
  type: NotificationType;
  group: NotificationGroup;
  /// Varsayılan kanallar. Kullanıcı tercihi bunu ezebilir — `critical` hariç.
  defaultChannels: NotificationChannel[];
  /**
   * BD6 — kritik bildirimler KAPATILAMAZ.
   * Kopuk hesap, ödeme hatası, kredi bitti, otomasyon hatası: kullanıcı
   * bunları görmezse ürün sessizce çalışmayı bırakır ve o bunu haftalar sonra
   * fark eder.
   */
  critical: boolean;
  /// Kime gider.
  audience: 'owners_admins' | 'all_members' | 'actor' | 'specific_user';
}

export const NOTIFICATION_CATALOG: Record<NotificationType, NotificationTypeDefinition> = {
  'automation.approval_pending': {
    type: 'automation.approval_pending',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.action_applied': {
    type: 'automation.action_applied',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.action_failed': {
    type: 'automation.action_failed',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'automation.run_skipped': {
    type: 'automation.run_skipped',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  // Varsayılan kanal `notify` aksiyonunun kendi `channels` alanıyla EZİLİYOR
  // (kural yazarı hangi kanalı istediğini zaten söylüyor). Buradaki değer
  // yalnız o alan verilmediğinde geçerli. `critical: false` — kullanıcının
  // kurduğu bir uyarıyı yine kullanıcı kapatabilmeli.
  'automation.rule_notified': {
    type: 'automation.rule_notified',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.disconnected': {
    type: 'connection.disconnected',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'connection.degraded': {
    type: 'connection.degraded',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.recovered': {
    type: 'connection.recovered',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'sync.failed': {
    type: 'sync.failed',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'creative.ready': {
    type: 'creative.ready',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'creative.failed': {
    type: 'creative.failed',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'actor',
  },
  'creative.refunded': {
    type: 'creative.refunded',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'actor',
  },
  // `audience: 'all_members'` — görevi bir KURAL açtı, bir kişi değil.
  // `actor` deseydik hedef, kuralı aylar önce kuran kişi olurdu; stüdyoya
  // bugün bakan başkasıysa görev sessizce beklerdi.
  'creative.task_opened': {
    type: 'creative.task_opened',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  // `owners_admins` — kararı verecek olanlar. `automation.approval_pending`
  // ile aynı hedef kitle ve aynı gerekçe: onay bir yönetim işi.
  'creative.approval_pending': {
    type: 'creative.approval_pending',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  // `actor` — kreatifi onaya gönderen kişi. Kararı ondan başkası beklemiyor.
  'creative.approval_decided': {
    type: 'creative.approval_decided',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // `actor` — analizi başlatan (ve 2 kredisini veren) kişi. Ekibin geri
  // kalanı o fotoğrafı hiç görmedi.
  //
  // ⚠️ Kanal yalnız `IN_APP` ve bu bilinçli: satırı yazan yer worker
  // (`vision-notify.ts`) ve orası `NotificationService`e ulaşamıyor, yani
  // e-posta gönderemiyor. Katalogda `EMAIL` yazsaydı tercih ekranı
  // kullanıcıya hiç gelmeyecek bir e-postayı açık/kapalı gösterirdi.
  'creative.vision_ready': {
    type: 'creative.vision_ready',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // `critical: false` — kredi zaten OTOMATİK iade ediliyor, kullanıcının
  // yapması gereken bir şey yok. Kapatılabilir olması doğru.
  'creative.vision_failed': {
    type: 'creative.vision_failed',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // Prodüksiyon — `actor`: başlatan (ve storyboard kredisini veren) kişi.
  // Grup `creative`: kullanıcı tercih ekranında reklam videosunu kreatif
  // üretiminin parçası olarak görüyor; ayrı sekme "kreatif bildirimlerini
  // kapattım ama video hâlâ yazıyor" demek olurdu.
  'production.question': {
    type: 'production.question',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'production.storyboard_ready': {
    type: 'production.storyboard_ready',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'production.delivered': {
    type: 'production.delivered',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // `critical: false` — `creative.vision_failed` ile aynı: rezerv OTOMATİK
  // iade ediliyor, kullanıcının yapması gereken bir şey yok.
  'production.failed': {
    type: 'production.failed',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // Görsel kampanya seti — video karşılıklarıyla AYNI kanal/kitle/kritiklik
  // (gerekçe tip listesinde): kullanıcı için ikisi de "kreatif üretimi".
  'production.set_question': {
    type: 'production.set_question',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'production.set_storyboard_ready': {
    type: 'production.set_storyboard_ready',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'production.set_delivered': {
    type: 'production.set_delivered',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'production.set_failed': {
    type: 'production.set_failed',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // `critical: false` — haber, eylem istemiyor; kullanıcı kapatabilir.
  'production.provider_wait': {
    type: 'production.provider_wait',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // Personele: SMS/WhatsApp yok (`support.ticket_assigned` gerekçesi); e-posta
  // var — bakiye yüklemesi panelden değil sağlayıcının sitesinden yapılıyor.
  'production.provider_blocked': {
    type: 'production.provider_blocked',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL'],
    critical: true,
    audience: 'specific_user',
  },
  'billing.credit_low': {
    type: 'billing.credit_low',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.credit_exhausted': {
    type: 'billing.credit_exhausted',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.limit_warning': {
    type: 'billing.limit_warning',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.grace_started': {
    type: 'billing.grace_started',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.grace_ended': {
    type: 'billing.grace_ended',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.payment_failed': {
    type: 'billing.payment_failed',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  // Süre uyarısı kapatılabilir (`critical: false`): kullanıcı denemenin
  // bittiğini zaten panelde görüyor ve bu bir HATIRLATMA. Bitişin kendisi
  // kapatılamaz — o an ürünün bir kısmı gerçekten duruyor.
  'billing.trial_ending': {
    type: 'billing.trial_ending',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.trial_ended': {
    type: 'billing.trial_ended',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'team.invitation_sent': {
    type: 'team.invitation_sent',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.member_joined': {
    type: 'team.member_joined',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.role_changed': {
    type: 'team.role_changed',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'specific_user',
  },
  'data.export_ready': {
    type: 'data.export_ready',
    group: 'data',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },

  // ═════════════════════════════════════════════════════════════════════════
  // 16 Eyl 2026 — kampanya / ajan / performans / hesap
  // ═════════════════════════════════════════════════════════════════════════
  //
  // 🔴 VARSAYILAN KANALLAR DÖRT: IN_APP + EMAIL + SMS + WHATSAPP.
  //
  // Kullanıcı kararı (16 Eyl 2026): "her olayda açık". Bu tablodaki HER satır
  // — yenisi de eskisi de — aynı dördü taşıyor. Bilinmesi gereken üç şey:
  //
  //   • SMS ÜCRETLİ ve mesaj başına. Aktif bir hesapta günde birkaç SMS
  //     demek; maliyeti düşürmek isteyen kullanıcı tercih ekranından
  //     kapatıyor, `critical` tipler hariç.
  //   • Adres YOKSA gönderim `SKIPPED` — hata değil. Kimsenin telefonu
  //     kayıtlı değilken bu satırlar hiçbir şeye mal olmuyor.
  //   • Kanal PLATFORM tarafında kapalıysa (bugün SMS ve WhatsApp öyle)
  //     yine `SKIPPED`. Yani bu varsayılanlar bir NİYET beyanı: kanal
  //     açıldığı gün akış kendiliğinden başlıyor, katalog tekrar
  //     düzenlenmiyor.
  'campaign.created': {
    type: 'campaign.created',
    group: 'campaign',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  'campaign.status_changed': {
    type: 'campaign.status_changed',
    group: 'campaign',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  // `owners_admins` — bütçe bir PARA kararı ve onu görmesi gereken, hesabın
  // parasından sorumlu olan. Yayının durması herkesi ilgilendiriyor (yukarıda
  // `all_members`), günlük bütçenin 200₺'den 400₺'ye çıkması ilgilendirmiyor.
  'campaign.budget_changed': {
    type: 'campaign.budget_changed',
    group: 'campaign',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'campaign.launch_failed': {
    type: 'campaign.launch_failed',
    group: 'campaign',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'ad.disapproved': {
    type: 'ad.disapproved',
    group: 'campaign',
    defaultChannels: ['IN_APP', 'EMAIL'],
    critical: false,
    audience: 'owners_admins',
  },

  // `audience: 'actor'` — öneriyi ajandan İSTEYEN kişi. Sohbeti o yürütüyor
  // ve kararı da o veriyor; ekibin geri kalanı konuşmayı hiç görmedi.
  'agent.approval_pending': {
    type: 'agent.approval_pending',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  // `owners_admins` — kararı yalnız yönetici verebilen kart (üye istedi).
  'agent.approval_needs_admin': {
    type: 'agent.approval_needs_admin',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'agent.optimization_applied': {
    type: 'agent.optimization_applied',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  // `critical` — ajan bir değişikliği uygulayamadıysa kullanıcı onu UYGULANMIŞ
  // sanıyor: onay ekranında "onaylandı" yazıyor ve kampanya eski hâlinde
  // kalıyor. Sessiz kalması, yapılmış sanılan bir işin hiç yapılmaması demek.
  'agent.optimization_failed': {
    type: 'agent.optimization_failed',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },

  // ── Otonom operatör (29 Eyl 2026) ──
  // Kitle `owners_admins`: operatör çalışma alanı adına ADMIN yetkisiyle
  // çalışıyor ve onay kararı `automation.approve` (ADMIN+) istiyor.
  // Hiçbiri `critical` değil — operatör kapatılabilir bir özellik; bildirimi
  // kapatılamaz yapmak, kullanıcıyı özelliği kapatmaya iterdi.
  'agent.operator_action_required': {
    type: 'agent.operator_action_required',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'agent.operator_applied': {
    type: 'agent.operator_applied',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  // Bulgu bir aksiyon DEĞİL: SMS/WhatsApp varsayılanı yok — kullanıcı
  // isterse tercih ekranından açar. Aksi hâlde her devriye telefonu çaldırırdı.
  'agent.operator_insight': {
    type: 'agent.operator_insight',
    group: 'agent',
    defaultChannels: ['IN_APP', 'EMAIL'],
    critical: false,
    audience: 'owners_admins',
  },

  // 🔴 Performans tiplerinin HİÇBİRİ `critical` DEĞİL ve bu bilinçli.
  //
  // Bunlar bir SEZGİSEL ölçümün çıktısı (dün vs. önceki 7 günün ortalaması,
  // eşik `PERFORMANCE_THRESHOLDS`). Sezgisel olan yanılır: kampanya yeni
  // açılmıştır, bayram geçmiştir, tek büyük sipariş ortalamayı bozmuştur.
  // Yanılabilen bir uyarıyı KAPATILAMAZ yapmak, kullanıcıyı zile bakmayı
  // bırakmaya iter — ve o an gerçek uyarılar da kaybolur.
  'performance.conversions_up': {
    type: 'performance.conversions_up',
    group: 'performance',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  'performance.conversions_down': {
    type: 'performance.conversions_down',
    group: 'performance',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'performance.cpa_spike': {
    type: 'performance.cpa_spike',
    group: 'performance',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'performance.spend_spike': {
    type: 'performance.spend_spike',
    group: 'performance',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'performance.roas_drop': {
    type: 'performance.roas_drop',
    group: 'performance',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },

  // 🔴 Üçü de `critical` VE `specific_user`.
  //
  // Kitle: olay hesabın SAHİBİNİN başına geliyor, kiracının değil. Bir
  // yönetici başkasının adres değişikliğini görmemeli — o bilgi güvenlik
  // uyarısı değil, gözetim olurdu.
  //
  // Kapatılamaz olmaları güvenlik gereği: bu üç bildirimin tek işlevi
  // "bunu ben yapmadım" dedirtmek. Kapatılabilseydi, hesabı ele geçiren
  // kişinin ilk yapacağı şey onları kapatmak olurdu.
  'account.email_changed': {
    type: 'account.email_changed',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.login_new_device': {
    type: 'account.login_new_device',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },

  // ── 16 Eyl 2026 — ince olay turu (gerekçeler tip listesinde) ──────────
  'account.login_failed': {
    type: 'account.login_failed',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.email_change_requested': {
    type: 'account.email_change_requested',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.session_revoked': {
    type: 'account.session_revoked',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'specific_user',
  },
  'account.sessions_revoked': {
    type: 'account.sessions_revoked',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.two_factor_enrolled': {
    type: 'account.two_factor_enrolled',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.two_factor_reset': {
    type: 'account.two_factor_reset',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.admin_login_blocked': {
    type: 'account.admin_login_blocked',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'account.admin_access_changed': {
    type: 'account.admin_access_changed',
    group: 'account',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'specific_user',
  },
  'team.invitation_resent': {
    type: 'team.invitation_resent',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.invitation_revoked': {
    type: 'team.invitation_revoked',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.member_removed': {
    type: 'team.member_removed',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.member_left': {
    type: 'team.member_left',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'team.ownership_transferred': {
    type: 'team.ownership_transferred',
    group: 'team',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'all_members',
  },
  'workspace.created': {
    type: 'workspace.created',
    group: 'workspace',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  'workspace.updated': {
    type: 'workspace.updated',
    group: 'workspace',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.created': {
    type: 'connection.created',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.removed': {
    type: 'connection.removed',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'connection.account_connected': {
    type: 'connection.account_connected',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.store_connected': {
    type: 'connection.store_connected',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'connection.store_disconnected': {
    type: 'connection.store_disconnected',
    group: 'connection',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'automation.rule_created': {
    type: 'automation.rule_created',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.rule_enabled': {
    type: 'automation.rule_enabled',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.rule_disabled': {
    type: 'automation.rule_disabled',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.rule_deleted': {
    type: 'automation.rule_deleted',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'automation.action_rejected': {
    type: 'automation.action_rejected',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'automation.action_rolled_back': {
    type: 'automation.action_rolled_back',
    group: 'automation',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.plan_changed': {
    type: 'billing.plan_changed',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.topup_purchased': {
    type: 'billing.topup_purchased',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.payment_recorded': {
    type: 'billing.payment_recorded',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.payment_refunded': {
    type: 'billing.payment_refunded',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.payment_disputed': {
    type: 'billing.payment_disputed',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: true,
    audience: 'owners_admins',
  },
  'billing.invoice_issued': {
    type: 'billing.invoice_issued',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  /* Hatırlatma zinciri — `critical: false` ama faturalama grubu "önemli":
     kullanıcı e-postayı/SMS'i kapatabilir, uygulama içi KAPANMAZ
     (`pinnedChannelsOf`). Tamamen kilitlemek ayda bir gelen bir hatırlatmayı
     susturmayı imkânsız kılardı; tamamen açmak "zile bile düşmüyor" şikâyetini
     geri getirirdi. */
  'billing.subscription_reminder': {
    type: 'billing.subscription_reminder',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.payment_overdue': {
    type: 'billing.payment_overdue',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'billing.grace_ending': {
    type: 'billing.grace_ending',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  /* Hedef yalnız OWNER (dinleyici rolle çağırıyor); katalogda "sahipler ve
     yöneticiler" en yakın kova — bakım `notify()`ı bu tipi göndermiyor. */
  'billing.renewal_requested': {
    type: 'billing.renewal_requested',
    group: 'billing',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'owners_admins',
  },
  'creative.published': {
    type: 'creative.published',
    group: 'creative',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'all_members',
  },
  'support.ticket_replied': {
    type: 'support.ticket_replied',
    group: 'support',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  'support.ticket_closed': {
    type: 'support.ticket_closed',
    group: 'support',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  /**
   * Talep açıldı makbuzu — kanal YALNIZ `IN_APP`.
   *
   * Kullanıcı talebi az önce kendi eliyle açtı; aynı saniyede bir e-posta
   * ve bir SMS göndermek, ürünün en gereksiz bildirimi olurdu. Zildeki satır
   * ise iş görüyor: talep numarasını taşıyor ve listeye götürüyor.
   */
  'support.ticket_created': {
    type: 'support.ticket_created',
    group: 'support',
    defaultChannels: ['IN_APP'],
    critical: false,
    audience: 'actor',
  },
  'support.ticket_resolved': {
    type: 'support.ticket_resolved',
    group: 'support',
    defaultChannels: ['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP'],
    critical: false,
    audience: 'actor',
  },
  /**
   * 🔴 İkisi de PERSONELE gidiyor ve `IN_APP` dışında kanalı YOK.
   *
   * Personelin telefonuna kiracı verisi taşıyan bir SMS göndermek, destek
   * içeriğini bizim kontrol etmediğimiz bir kanala çıkarmak demekti — ve
   * gövde şifrelenirken konunun SMS ile gitmesi, şifrelemenin amacını
   * boşa çıkarırdı. Kuyruk uyarısı için e-posta zaten ayrı bir yoldan
   * (`SUPPORT_TEAM_EMAIL`) gidiyor ve o adres bizim.
   *
   * `critical: true` — atanan bileti personelin GÖRMEMESİ, biletin
   * kimsenin bakmadığı bir kuyrukta beklemesi demek. Kapatılabilir olsaydı
   * ilk kapatan kişi kendi kuyruğunu görünmez yapardı.
   */
  'support.ticket_assigned': {
    type: 'support.ticket_assigned',
    group: 'support',
    defaultChannels: ['IN_APP'],
    critical: true,
    audience: 'specific_user',
  },
  'support.ticket_sla_breached': {
    type: 'support.ticket_sla_breached',
    group: 'support',
    defaultChannels: ['IN_APP'],
    critical: true,
    audience: 'specific_user',
  },
};

/**
 * "ÖNEMLİ" — kritik ile serbest arasındaki basamak (1 Eki 2026).
 *
 * Kritik tip (`critical: true`) her kanalda kilitli. Faturalama grubunun
 * kritik OLMAYAN tipleri (hatırlatmalar, deneme bitiyor, kredi azalıyor,
 * ödeme alındı…) "önemli": kullanıcı e-postayı/SMS'i kapatabilir ama
 * UYGULAMA İÇİ her zaman açık — parayla ilgili bir haber zilden de
 * kaybolursa kullanıcı planının bittiğini hiçbir yerde görmüyordu.
 *
 * Tercih çözümü (`resolveChannels`), dağıtıcı (`channelsFor`) ve tercih
 * ekranı (`NotificationPreference.lockedChannels`) aynı fonksiyonu okuyor.
 */
export function pinnedChannelsOf(type: NotificationType): NotificationChannel[] {
  const definition = NOTIFICATION_CATALOG[type];
  if (!definition || definition.critical) return [];
  return definition.group === 'billing' ? ['IN_APP'] : [];
}

/**
 * PERFORMANS EŞİKLERİ — `apps/worker/src/performance/` tek kaynağı burası.
 *
 * Eşikler `shared`ta çünkü arayüz de aynı sayıyı yazıyor ("dünkü dönüşüm
 * önceki haftaya göre %30 arttığında haber veriyoruz"). İki yerde durursa
 * biri değiştiğinde ürün kullanıcıya yalan söyler.
 *
 * 🔴 `minSpend` ve `minConversions` GÜRÜLTÜ SÜZGECİ, keyfi değil: 12₺
 * harcayıp 1 dönüşüm alan bir kampanyada dönüşüm 2'ye çıkınca "%100 artış"
 * doğru ama ANLAMSIZ. Alt sınır olmadan bildirimlerin çoğu böyle satırlardan
 * gelirdi ve gerçek sıçramalar aralarında kaybolurdu.
 */
export const PERFORMANCE_THRESHOLDS = {
  /// Karşılaştırma penceresi: dün, ondan önceki 7 günün günlük ortalamasına
  /// karşı. 7 gün haftanın günü etkisini düzleştiriyor (hafta sonu trafiği
  /// hafta içinden farklı ve 1 günlük karşılaştırma bunu her pazartesi
  /// "sıçrama" diye okur).
  baselineDays: 7,
  /// Yüzde olarak. Dönüşüm ±%30, maliyet +%40, harcama +%50, ROAS −%30.
  conversionsUpPct: 30,
  conversionsDownPct: 30,
  cpaSpikePct: 40,
  spendSpikePct: 50,
  roasDropPct: 30,
  /// Taban penceresinde en az bu kadar harcama olmalı (hesabın para birimi).
  minSpend: 100,
  /// Ve en az bu kadar dönüşüm — yoksa oran hesabı tek siparişe bağlı kalır.
  minConversions: 3,
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NotificationService imzası — DİĞER ALANLARIN SÖZLEŞMESİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `NotificationService.notify(input: NotifyInput): Promise<void>`
 *
 * Bildirim üreten alanlar (automation, integrations, billing, creative,
 * workspaces) bu tek metodu çağırır. Kimse `prisma.notification.create()`
 * yazmaz — yazarsa kanal tercihi, dedupe ve kritik-bildirim kuralı atlanır.
 *
 * `Notification.userId` nullable olduğu için workspace geneli bildirim
 * şemada zaten mümkün; `target.type = 'workspace'` bunu kullanır.
 */
export interface NotifyInput {
  workspaceId: string;
  target:
    | { type: 'user'; userId: string }
    | { type: 'role'; roles: ('OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER')[] }
    | { type: 'workspace' };
  type: NotificationType;
  /// i18n anahtarları. Katalogdan türetilebilir ama açıkça verilmesi
  /// mümkün — özel metinli `notify` aksiyonu bunu kullanıyor.
  titleKey?: string;
  bodyKey?: string;
  /// Çeviriye giden değişkenler ("CPC $2.40", "Meta Ads Hesabı").
  params?: Record<string, string | number>;
  /// BD4 — kaynağa sıçrama. HAM URL SAKLANMAZ; rota değişince eski
  /// bildirimler kırılır. Arayüz rotayı `entity` + `id`'den üretir.
  data?: {
    /// 🔴 `account` (16 Eyl 2026) güvenlik bildirimlerinin hedefi ve tek
    /// varlık ki KİMLİĞİ anlamsız: "parolan değişti" bildirimi kullanıcıyı
    /// kendi güvenlik ayarlarına götürüyor, bir kaydın detayına değil.
    /// Rota eşlemesi `apps/web/src/lib/notification-api.ts`te — orada
    /// karşılığı olmayan varlık bağlantısız satır demek.
    entity:
      | 'campaign'
      | 'workflow'
      | 'workflow_action'
      | 'creative'
      | 'vision_analysis'
      /** Prodüksiyon (reklam videosu, Faz 2) — `id` prodüksiyon kimliği. */
      | 'production'
      | 'connection'
      | 'invoice'
      | 'member'
      | 'account'
      /**
       * 🔴 17 Eyl 2026 — destek bildirimleri `'member'` diyordu.
       *
       * Yani "destek yanıtladı" bildirimine tıklayan kullanıcı EKİP
       * AYARLARINA (`/dashboard/settings/team`) gidiyordu. Kod derleniyordu,
       * test kırmıyordu ve rota gerçekten var olduğu için sonsuz yönlendirme
       * de üretmiyordu — yanlış ekran açılıyordu, o kadar. Tam olarak bu
       * yüzden fark edilmedi.
       *
       * Rota KİMLİKSİZ (`/dashboard/support`): bilet detayının kendi sayfası
       * henüz yok ve olmayan bir yola bağlanmak, köprü yüzünden 404 değil
       * SONSUZ YÖNLENDİRME üretir (`notification-route.ts` başındaki uyarı).
       */
      | 'support_ticket'
      /** Otonom operatörün koşusu (29 Eyl 2026) → `/dashboard/agent/operator`. */
      | 'operator_run'
      /**
       * AI sohbeti (1 Eki 2026) → `/dashboard/agent?c=<id>`. Takip görevinin
       * sonucu KAYNAK SOHBETE yazılıyor; bildirim de oraya götürüyor (koşu
       * ekranına değil — kullanıcı sonucu sohbetinde okuyor).
       */
      | 'agent_conversation'
      /**
       * Abonelik hatırlatması (1 Eki 2026) → ödeme sayfası, plan seçici AÇIK
       * (`?plans=open`). Kimlik çalışma alanı; rota kullanmıyor.
       */
      | 'subscription';
    id: string;
    [key: string]: unknown;
  };
  /// Katalog varsayılanını ezer. Kritik tiplerde EMAIL kanalı çıkarılamaz.
  channels?: NotificationChannel[];
  /**
   * Gürültü kontrolü. Aynı anahtar TTL süresince ikinci kez bildirim üretmez.
   * Olmadan bildirim listesi kullanılamaz hâle gelir: senkron 15 dakikada bir
   * deniyor ve her denemede "bağlantı koptu" yazsaydı gün sonunda 96 satır.
   */
  dedupeKey?: string;
  dedupeTtlSeconds?: number;
}

/// Önerilen dedupe pencereleri.
export const DEDUPE_TTL = {
  connectionDisconnected: 15 * 60,
  syncFailed: 60 * 60,
  automationActionFailed: 60 * 60,
  /// Dönem başına tek uyarı.
  creditLow: 30 * 24 * 60 * 60,
  /**
   * Performans uyarıları — 20 saat (16 Eyl 2026).
   *
   * Tarayıcı günde bir koşuyor, yani teorik olarak dedupe'a gerek yok. Ama
   * elle tetikleme, yeniden deneme ve worker'ın iki kopya çalıştığı bir an
   * aynı günün uyarısını ikinci kez üretir. 24 değil 20 saat: tam gün
   * yazsaydık, tarama bir gün 04:05'te bir gün 03:55'te koştuğunda ikinci
   * gününki pencereye çarpıp SESSİZCE düşerdi — kaçan uyarı, tekrarlanandan
   * kötü.
   */
  performanceScan: 20 * 60 * 60,
  /**
   * Kampanya durum/bütçe değişikliği — 5 dakika, HEDEF BAŞINA.
   *
   * Toplu işlem tek bir istekte 40 kampanyayı duraklatabiliyor ve her biri
   * ayrı bildirim üretir; bu doğru (kullanıcı hangilerinin durduğunu görmeli).
   * Pencere, aynı kampanyaya arka arkaya basılan düğmeye karşı: arayüzde çift
   * tıklama ve yeniden deneme aynı satırı iki kez yazardı.
   */
  campaignChange: 5 * 60,
  /**
   * Prodüksiyon kapısı — 24 saat, anahtar `productionNotifyDedupeKey`
   * (kimlik + tip + `version`). Aynı kapı için yeniden denenen aşama işi
   * ikinci bildirimi üretmez; kullanıcı düzenleyip kapı yeniden açılırsa
   * `version` arttığı için yeni anahtar — yeni bildirim meşru.
   */
  production: 24 * 60 * 60,
  /**
   * Prodüksiyon TESLİMİ — 90 gün, anahtar KURGU başına (sürüm değil). Teslim
   * edilmiş filme revizyon istenip reddedildiğinde (ya da rızası verilmediğinde)
   * prodüksiyon önceki teslimine döner; 24 saatlik pencere geçmişse kullanıcı
   * aynı film için ikinci bir "reklam videon hazır" alırdı. Yeni kurgu yeni
   * anahtar — onun bildirimi meşru.
   */
  productionDelivered: 90 * 24 * 60 * 60,
  /**
   * Sağlayıcı beklemesinin YÖNETİCİ uyarısı — 6 saat, anahtar sağlayıcı +
   * yönetici başına. Aynı kesintide beş prodüksiyon beklemeye girerse yönetici
   * beş değil bir uyarı alır; bakiye yüklenmeden altı saat geçerse hatırlatma.
   * Kullanıcının haberi bekleme başına bir kez (anahtarda başlangıç anı).
   */
  productionProviderBlocked: 6 * 60 * 60,
  /**
   * Kapı hatırlatması (`PRODUCTION_GATE_IDLE_REMIND_DAYS`) — kapı + sürüm başına
   * TEK hatırlatma: pencere hatırlatma ile iptal arasındaki süreden uzun (8 gün).
   */
  productionGateReminder: 8 * 24 * 60 * 60,
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// Uçlar
// ═══════════════════════════════════════════════════════════════════════════

export interface NotificationRow {
  id: string;
  type: string;
  group: NotificationGroup | null;
  /// İngilizce yedek metin (yukarıdaki çok dillilik kararına bak).
  title: string;
  body: string | null;
  /// Arayüzün çeviri için kullanacağı asıl veri.
  params: Record<string, string | number> | null;
  data: Record<string, unknown> | null;
  channel: NotificationChannel;
  readAt: string | null;
  sentAt: string | null;
  critical: boolean;
  createdAt: string;
}

export const notificationListQuerySchema = listQuerySchema.extend({
  unreadOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  group: z.enum(NOTIFICATION_GROUPS).optional(),
  type: z.string().max(60).optional(),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

/// KB6 — kabuktaki zil her sayfada çağırıyor; ayrı ve hafif uç.
export interface UnreadCountResponse {
  unread: number;
  /// Gruba göre kırılım — zil menüsü sekmelerini besliyor.
  byGroup: Record<NotificationGroup, number>;
  /// Okunmamış KRİTİK var mı — zil kırmızı yanar.
  hasCritical: boolean;
}

export const markReadSchema = z.object({
  /// Boş bırakılırsa (ya da `all: true`) hepsi okundu işaretlenir.
  ids: z.array(z.uuid()).max(200).optional(),
  all: z.boolean().default(false),
  group: z.enum(NOTIFICATION_GROUPS).optional(),
});
export type MarkReadInput = z.infer<typeof markReadSchema>;

/**
 * BD5 — kanal tercihleri.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ✅ Redis → tablo → KANAL DİZİSİ (üç aşama, sonuncusu 9 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * Faz 1'de tercihler Redis hash'indeydi (şema donmuştu); Faz 3'te
 * `notification_preferences` tablosuna taşındı; 9 Eyl 2026'da tablonun
 * `inApp`/`email` boolean kolonları `channels NotificationChannel[]` dizisine
 * dönüştü.
 *
 * Son adımın gerekçesi: iki boolean, kanal sayısının ikiden fazla
 * olamayacağını varsayıyordu. SMS, Telegram ve Webhook eklenince her yeni
 * kanal bir migration + beş dosyada beş yeni satır demek olurdu. Dizi ile
 * kanal eklemek enum'a bir değer; bu sözleşme HİÇ değişmiyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 BOŞ DİZİ ile TERCİH YOKLUĞU AYRI ŞEYLER
 * ---------------------------------------------------------------------------
 *   • yanıtta `channels` her zaman dolu — çözülmüş HÂLİ döner
 *     (kullanıcı dokunmadıysa katalog varsayılanı).
 *   • istekte `channels: []` = "bu tipi tümüyle kapat" ve tabloya boş dizi
 *     yazılır. Varsayılana DÖNMEK isteyen istek tipi hiç göndermez.
 */
export interface NotificationPreference {
  type: NotificationType;
  group: NotificationGroup;
  /// Bu tip için AÇIK olan kanallar — çözülmüş hâl.
  channels: NotificationChannel[];
  /// Kullanıcının seçebileceği kanallar. Katalog varsayılanından bağımsız:
  /// platform yöneticisi SMS'i kapattıysa bu listede SMS yok ve ekran
  /// kapatılmış bir kanalı açık/kapalı göstermiyor.
  availableChannels: NotificationChannel[];
  /// Kritik tipler `locked: true` döner ve PUT'ta YOK SAYILIR.
  locked: boolean;
  /**
   * Tip kilitli değilken bile KAPATILAMAYAN kanallar ("önemli" — faturalama
   * grubunda uygulama içi, `pinnedChannelsOf`). Eski istemciler alanı
   * bilmiyor: sunucu PUT'ta bu kanalları yine ekliyor.
   */
  lockedChannels?: NotificationChannel[];
}

export const preferencesUpdateSchema = z.object({
  preferences: z
    .array(
      z.object({
        type: z.enum(NOTIFICATION_TYPES),
        channels: z.array(notificationChannelSchema).max(16),
      }),
    )
    .max(NOTIFICATION_TYPES.length),
});
export type PreferencesUpdateInput = z.infer<typeof preferencesUpdateSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Admin — SALT OKUNUR envanter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `/admin/notifications` bu turda yalnız GÖRÜNTÜLER: hangi bildirim tipleri
 * var, hangileri kritik, hangi e-posta şablonları tanımlı, SMTP açık mı.
 *
 * "Herkese duyuru gönder" düğmesi YAZILMAZ: test edilmemiş bir toplu gönderim
 * yüzeyi, yanlış basılınca geri alınamaz. POSTA şablonu DÜZENLEME de yok —
 * şablonlar `@mentorivo/shared/mail`de kod sabiti ve düzenlemeyi saklayacak bir
 * tablo yok; düzenleme ekranı yalan söyleyen bir düğme olurdu.
 *
 * ⚠️ Faz 3'te OTOMASYON şablonları (`/admin/templates`) düzenlenebilir oldu;
 * bu ekran değil. Otomasyon şablonu VERİ, posta şablonu satır içi stilli HTML —
 * ikincisini metin kutusundan düzenletmek, bozuk bir gövdenin doğrudan gelen
 * kutusuna gitmesi demekti.
 */
export interface AdminNotificationTypeRow extends NotificationTypeDefinition {
  /// Bu tipten kaç bildirim üretilmiş (tüm kiracılar).
  totalCount: number;
  lastSentAt: string | null;
}

/**
 * ⚠️ 10 Eyl 2026 — `templates` alanı KALDIRILDI ve `smtp` şekil değiştirdi.
 *
 * `templates` `mail.ts`teki sabit şablonların envanteriydi (`auth.verify`,
 * `notification.*`). O şablonlar silindi; gerçek envanter artık
 * `/admin/notifications/templates`te ve tablodan okunuyor.
 *
 * `smtp.lastSendOk/lastAttemptAt` bir Redis anahtarından okunuyordu ve o
 * anahtarı yazan `NotificationMailer.deliver`ı 9 Eyl'den beri kimse
 * çağırmıyordu — gösterge, son gerçek gönderimden bağımsız olarak DONMUŞTU.
 * Yerine `notification_channel_configs` EMAIL satırı geçti: bildirim
 * sürücüsü ve sistem postaları (doğrulama, parola) sonucu oraya yazıyor.
 */
export interface AdminNotificationsOverview {
  types: AdminNotificationTypeRow[];
  smtp: {
    /// SMTP_HOST dolu mu. DEĞER DÖNMEZ.
    configured: boolean;
    /// EMAIL kanalının sağlığı — son gönderimde ne olduysa o.
    status: ConnectionStatus;
    /// Son BAŞARILI gönderim. `null` + `lastError: null` = hiç gönderim yok.
    lastDeliveredAt: string | null;
    /// Son başarısız gönderimin sebebi; başarılı bir gönderim onu siliyor.
    lastError: string | null;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DİL KAYDI — çalışma zamanında eklenebilen diller
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 DİL ARTIK BİR ENUM DEĞİL, BİR SATIR.
 *
 * `Locale` tipi (`locales.ts`, 29 Eyl 2026'dan beri on üç dil) arayüzün rota
 * dillerini ve kod içindeki YEDEK metinlerin dillerini anlatıyor. Bildirimin
 * gideceği dil kümesi ise `notification_locales` tablosunda ve oraya bir satır
 * eklemek deploy gerektirmiyor. On üç dilin satırı migration'la açıldı
 * (`20260929120000_bildirim_dilleri_13`).
 *
 * İkisi arasındaki ilişki: tabloya "de" eklendiğinde Almanca şablonu OLAN
 * bildirimler Almanca, olmayanlar `fallbackCode` → varsayılan dil zinciriyle
 * mevcut bir dile düşüyor. Yani eksik çeviri ürünü BOZMUYOR.
 */
export interface NotificationLocaleRow {
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  enabled: boolean;
  isDefault: boolean;
  fallbackCode: string | null;
  sortOrder: number;
  /// Bu dilde KAÇ şablon tanımlı — yönetici listesinde "12/217" gibi bir
  /// doluluk göstergesi. Sayı olmadan "diller eklendi ama boş" durumu
  /// yalnız tek tek açarak fark edilirdi.
  templateCount: number;
  /// Kod yedeği bu dili biliyor mu (`BUILTIN_COPY_LOCALES`). Bilmiyorsa
  /// şablonu olmayan her bildirim varsayılan dile düşüyor ve ekran bunu
  /// söylüyor.
  hasBuiltinCopy: boolean;
}

/// BCP-47 biçimi: "tr", "pt-BR", "zh-Hans". Serbest dize değil — geçersiz bir
/// kod `User.locale` ile eşleşmez ve o dil hiç kullanılmaz.
const localeCodePattern = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

export const notificationLocaleCreateSchema = z.object({
  code: z.string().min(2).max(20).regex(localeCodePattern, 'Geçersiz dil kodu (BCP-47 bekleniyor)'),
  name: z.string().min(1).max(80),
  nativeName: z.string().min(1).max(80),
  rtl: z.boolean().default(false),
  fallbackCode: z.string().max(20).nullable().default(null),
  sortOrder: z.number().int().min(0).max(999).default(0),
});
export type NotificationLocaleCreateInput = z.infer<typeof notificationLocaleCreateSchema>;

export const notificationLocaleUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  nativeName: z.string().min(1).max(80).optional(),
  rtl: z.boolean().optional(),
  enabled: z.boolean().optional(),
  fallbackCode: z.string().max(20).nullable().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
  /// `true` gönderildiğinde eski varsayılan tek transaction içinde indiriliyor.
  /// `false` gönderilemiyor: varsayılansız bir sistemde şablonu bulunamayan
  /// bildirimin düşeceği yer kalmazdı.
  isDefault: z.literal(true).optional(),
});
export type NotificationLocaleUpdateInput = z.infer<typeof notificationLocaleUpdateSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ŞABLONLAR
// ═══════════════════════════════════════════════════════════════════════════

/// Şablonun kimliği — (anahtar × kanal × dil).
export interface NotificationTemplateRow {
  id: string;
  key: string;
  channel: NotificationChannel;
  localeCode: string;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  enabled: boolean;
  version: number;
  updatedAt: string;
  updatedByName: string | null;
}

/**
 * Şablon ekranının bir SATIRI — bir anahtar, bütün kanallar ve diller için
 * doluluk özeti.
 *
 * Yönetici ekranı 30+ bildirim tipi × 7 kanal × N dil = yüzlerce hücre
 * gösteriyor. Hepsini tek tek satır olarak dönmek yanıtı megabaytlara
 * çıkarırdı; bu özet "hangi hücreler dolu" sorusunu cevaplıyor ve içerik
 * ancak bir hücre açıldığında geliyor.
 */
export interface NotificationTemplateSummary {
  key: string;
  /// Katalogdan; `system.*` anahtarlarında `null`.
  group: NotificationGroup | null;
  critical: boolean;
  /// Bu anahtarın metninde kullanılabilen değişkenler ("ruleName", "platform").
  placeholders: string[];
  /// "EMAIL:tr" biçiminde, TANIMLI olan hücreler.
  definedCells: string[];
}

export const notificationTemplateUpsertSchema = z.object({
  key: z.string().min(3).max(80),
  channel: notificationChannelSchema,
  localeCode: z.string().min(2).max(20),
  subject: z.string().max(300).nullable().default(null),
  /// 🔴 64 KB tavan. Sınırsız bırakmak, yapıştırılan bir sayfa tasarımının
  /// (görselleri base64 gömülü) tek satırda megabaytlarca yer kaplaması
  /// demekti — ve o gövde her gönderimde SMTP'den geçerdi.
  bodyHtml: z.string().max(65536).nullable().default(null),
  bodyText: z.string().max(8000).nullable().default(null),
  enabled: z.boolean().default(true),
});
export type NotificationTemplateUpsertInput = z.infer<typeof notificationTemplateUpsertSchema>;

/// Kaydetme yanıtı. `removed` boş değilse ekran "şunlar çıkarıldı" uyarısı
/// gösteriyor — sessiz temizlik en kötü seçenekti.
export interface NotificationTemplateSaveResult {
  template: NotificationTemplateRow;
  /// `sanitizeTemplateHtml`in attığı etiket/öznitelikler.
  removed: string[];
  /// Metin kanalın sınırına sığmıyor (SMS 640, Slack 3000…). Kaydetmeyi
  /// ENGELLEMİYOR — örnek parametrelerle ölçülen bir tahmin ve gerçek
  /// parametreler daha kısa olabilir.
  lengthWarning: { limit: number; rendered: number } | null;
}

export interface NotificationTemplateVersionRow {
  version: number;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  editedByName: string | null;
  createdAt: string;
}

export const notificationTemplatePreviewSchema = z.object({
  key: z.string().min(3).max(80),
  channel: notificationChannelSchema,
  localeCode: z.string().min(2).max(20),
  /// Kaydedilmemiş taslağı önizlemek için. Verilmezse kayıtlı şablon (ya da
  /// kod yedeği) render ediliyor.
  subject: z.string().max(300).nullable().optional(),
  bodyHtml: z.string().max(65536).nullable().optional(),
  bodyText: z.string().max(8000).nullable().optional(),
  /// Örnek değerler. Verilmeyen yer tutucular katalogdan üretilen örneklerle
  /// doluyor — boş bırakmak, önizlemeyi gerçek e-postadan farklı gösterirdi.
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
export type NotificationTemplatePreviewInput = z.infer<typeof notificationTemplatePreviewSchema>;

export interface NotificationTemplatePreview {
  subject: string | null;
  html: string | null;
  text: string;
  truncated: boolean;
  removed: string[];
  /// Hangi kaynaktan render edildi — ekran "bu hücre boş, İngilizceye
  /// düşüyor" diyebilsin diye.
  source: 'draft' | 'template' | 'fallback-locale' | 'builtin';
  /// Yedek zinciri uygulandıysa gerçekte kullanılan dil.
  resolvedLocale: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// KANAL YAPILANDIRMASI — "biz hangi kanalları açtık"
// ═══════════════════════════════════════════════════════════════════════════

export interface NotificationChannelRow {
  channel: NotificationChannel;
  label: string;
  enabled: boolean;
  provider: string | null;
  /// Sırları MASKELİ yapılandırma (`maskChannelConfig`).
  config: Record<string, unknown> | null;
  /// Sağlayıcı yapılandırması gerekiyor ve girilmiş mi. `enabled && !configured`
  /// = açık ama çalışmayan kanal; ekran bunu kırmızı gösteriyor.
  requiresConfig: boolean;
  configured: boolean;
  status: string;
  lastError: string | null;
  lastDeliveredAt: string | null;
  /// Son 24 saatte bu kanaldan kaç gönderim, kaçı başarısız.
  last24h: { sent: number; failed: number; skipped: number };
}

/**
 * ⚠️ `rateLimitPerMinute` 10 Eyl 2026'da sözleşmeden ÇIKTI.
 *
 * Alan kaydediliyor, yanıtta dönüyor ve HİÇBİR YERDE UYGULANMIYORDU: dağıtıcı
 * kanal başına sırayla ve anında gönderiyor, okuyan bir kuyruk yok. Formda
 * duran bir "dakikada en fazla 30 SMS" kutusu, yöneticiye sağlayıcıyı
 * koruduğunu sanan bir güven veriyordu. DB kolonu duruyor (şemada not var);
 * gerçek hız sınırı kanal başına yeniden deneme kuyruğuyla birlikte ayrı iş.
 */
export const notificationChannelUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  /// Sağlayıcı kimlik bilgileri. Kanal başına şekli farklı
  /// (`channelProviderConfigSchema`). Gönderilmezse kayıtlı değer KORUNUYOR —
  /// yönetici yalnız `enabled` değiştirmek için parolayı yeniden yazmamalı.
  config: channelProviderConfigSchema.optional(),
});
export type NotificationChannelUpdateInput = z.infer<typeof notificationChannelUpdateSchema>;

/// Yöneticinin "gerçekten çalışıyor mu" düğmesi. Kimlik bilgisini kaydetmeden
/// önce denemek mümkün değil (sır istekte taşınmıyor); bu uç KAYITLI
/// yapılandırmayla tek bir mesaj gönderiyor.
export const notificationChannelTestSchema = z.object({
  /// Nereye — telefon, sohbet kimliği, e-posta. Kanalın adreslemesine göre
  /// zorunlu; `workspace` adresli kanallarda (Slack) yok sayılıyor.
  destination: z.string().min(1).max(200).optional(),
  localeCode: z.string().min(2).max(20).default('tr'),
});
export type NotificationChannelTestInput = z.infer<typeof notificationChannelTestSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// KİŞİ ADRESLERİ — kullanıcının telefonu, Telegram sohbeti
// ═══════════════════════════════════════════════════════════════════════════

export interface NotificationContactRow {
  channel: NotificationChannel;
  /// MASKELİ. Tam adres yalnız sahibinin girdiği anda biliniyor; ekran
  /// "+90555****233" gösteriyor ve değiştirmek yeniden yazmayı gerektiriyor.
  address: string;
  verified: boolean;
  verifiedAt: string | null;
  /// Doğrulama kodu gönderildi ve hâlâ geçerli mi — ekran "kodu gir"
  /// kutusunu buna göre açıyor.
  verificationPending: boolean;
  /// Kanal platform tarafında açık mı. Kapalıysa adres kaydedilebilir ama
  /// bildirim gitmez; ekran bunu söylüyor.
  channelEnabled: boolean;
  /**
   * Adres nasıl doğrulanıyor.
   *
   *   `code` → numarayı yaz, kanaldan gelen altı haneli kodu gir (SMS, WhatsApp)
   *   `link` → bağlantıyı aç, bota "Başlat" de (Telegram)
   *
   * Ekran bunu kanal adına bakarak ÇIKARMIYOR: yarın WhatsApp da bağlantılı
   * doğrulamaya geçerse arayüz tek satır değişmeden doğru akışı çizmeli.
   */
  verifyMethod: 'code' | 'link';
  /**
   * Kişisel adres yokken bildirimin düştüğü KİRACI yedeği — yalnız WHATSAPP.
   *
   * 18 Ağu 2026'dan beri kiracı "Bağlantılar" ekranında TEK bir WhatsApp
   * alıcısı kaydedebiliyor; 9 Eyl'de adres kişiye taşındı ve o kayıt YEDEĞE
   * düştü: kişisel numarası olmayan üyenin bildirimi oraya gidiyor, olan
   * üyeninki kendi numarasına. Ekran bunu söylemezse kullanıcı numarasını
   * ekleyince bildirimlerin neden "başka telefona" gitmeyi bıraktığını
   * anlayamaz. Adres MASKELİ.
   */
  workspaceFallback: { displayName: string | null; address: string } | null;
}

/**
 * Telegram bağlantı düğmesinin adresi — `POST /notifications/contacts/telegram`.
 *
 * `url` = `https://t.me/<bot>?start=<jeton>`. Jeton TEK KULLANIMLIK ve 10
 * dakika yaşıyor; kullanıcı bota "Başlat" dediğinde sohbet kimliği o mesajdan
 * öğreniliyor ve adres doğrulanmış olarak yazılıyor. Ekran `expiresAt`e kadar
 * adres listesini yokluyor.
 */
export interface NotificationTelegramLink {
  url: string;
  expiresAt: string;
}

export const notificationContactUpsertSchema = z.object({
  channel: notificationChannelSchema,
  /// Ham adres — normalleştirme ve biçim doğrulaması sunucuda, kanal
  /// sürücüsünde (`normalizeAddress`). İstemcide yapmak, iki farklı
  /// normalleştirme ve "kaydettim ama gitmiyor" demekti.
  address: z.string().min(3).max(200),
});
export type NotificationContactUpsertInput = z.infer<typeof notificationContactUpsertSchema>;

export const notificationContactVerifySchema = z.object({
  channel: notificationChannelSchema,
  code: z.string().min(4).max(10),
});
export type NotificationContactVerifyInput = z.infer<typeof notificationContactVerifySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// GÖNDERİM DEFTERİ
// ═══════════════════════════════════════════════════════════════════════════

export const NOTIFICATION_DELIVERY_STATUSES = ['PENDING', 'SENT', 'FAILED', 'SKIPPED'] as const;
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/**
 * Gönderim defterinin saklama kuralı — worker'ın günlük temizliği okuyor
 * (`apps/worker/src/notify/delivery-purge.service.ts`).
 *
 * 🔴 `FAILED` LİSTEDE YOK ve olmamalı: arıza kaydının geçmişi silinmemeli —
 * "bu kanal ne zamandır bozuk" sorusu genellikle aylar sonra soruluyor.
 * `PENDING` de yok: bekleyen bir satır bitmemiş bir iş, eski olsa bile.
 *
 * Kural burada çünkü defteri YAZAN API, SİLEN worker; iki sürecin aynı sayıyı
 * iki yerde tutması, birinin 90'ı 30'a çektiği gün diğerinin bilmemesi demekti.
 */
export const NOTIFICATION_DELIVERY_RETENTION_DAYS = 90;
export const NOTIFICATION_DELIVERY_PURGEABLE_STATUSES = [
  'SENT',
  'SKIPPED',
] as const satisfies readonly NotificationDeliveryStatus[];

export interface NotificationDeliveryRow {
  id: string;
  channel: NotificationChannel;
  templateKey: string;
  localeCode: string;
  /// Maskeli hedef.
  destination: string | null;
  status: NotificationDeliveryStatus;
  attempts: number;
  errorCode: string | null;
  /// Ham sağlayıcı metni — YALNIZ platform yöneticisine dönüyor. Kiracı
  /// ekranında `errorCode` var, metin yok (sağlayıcı hatası kullanıcıya
  /// gösterilmez kuralı).
  error: string | null;
  sentAt: string | null;
  createdAt: string;
  /// Kime gitti — yalnız admin listesinde dolu.
  userName: string | null;
}

export const notificationDeliveryListQuerySchema = listQuerySchema.extend({
  channel: notificationChannelSchema.optional(),
  status: z.enum(NOTIFICATION_DELIVERY_STATUSES).optional(),
  templateKey: z.string().max(80).optional(),
});
export type NotificationDeliveryListQuery = z.infer<typeof notificationDeliveryListQuerySchema>;

export const NotificationErrorCode = {
  NOTIFICATION_NOT_FOUND: 'NOTIFICATION_NOT_FOUND',
  /// Kritik tipin kanalı kapatılamaz — istek kısmen uygulanır ve bu kod
  /// `details.ignoredTypes` ile döner.
  CRITICAL_TYPE_LOCKED: 'CRITICAL_TYPE_LOCKED',
  UNKNOWN_TYPE: 'UNKNOWN_TYPE',

  // ── Dil kaydı ──────────────────────────────────────────────────────────
  LOCALE_NOT_FOUND: 'LOCALE_NOT_FOUND',
  LOCALE_EXISTS: 'LOCALE_EXISTS',
  /// Varsayılan dil silinemez/kapatılamaz — düşülecek yer kalmazdı.
  LOCALE_IS_DEFAULT: 'LOCALE_IS_DEFAULT',
  /// `fallbackCode` zinciri kendine dönüyor.
  LOCALE_FALLBACK_CYCLE: 'LOCALE_FALLBACK_CYCLE',

  // ── Şablon ─────────────────────────────────────────────────────────────
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  /// Kanalın desteklemediği alan gönderildi (SMS'e `bodyHtml` gibi).
  TEMPLATE_FIELD_UNSUPPORTED: 'TEMPLATE_FIELD_UNSUPPORTED',
  TEMPLATE_VERSION_NOT_FOUND: 'TEMPLATE_VERSION_NOT_FOUND',

  // ── Kanal ──────────────────────────────────────────────────────────────
  CHANNEL_NOT_CONFIGURED: 'CHANNEL_NOT_CONFIGURED',
  CHANNEL_DISABLED: 'CHANNEL_DISABLED',
  CHANNEL_TEST_FAILED: 'CHANNEL_TEST_FAILED',

  // ── Adres ──────────────────────────────────────────────────────────────
  CONTACT_INVALID_ADDRESS: 'CONTACT_INVALID_ADDRESS',
  CONTACT_NOT_FOUND: 'CONTACT_NOT_FOUND',
  CONTACT_CODE_INVALID: 'CONTACT_CODE_INVALID',
  CONTACT_CODE_EXPIRED: 'CONTACT_CODE_EXPIRED',
  /// Kod isteme sıklığı sınırı — SMS her denemede para.
  CONTACT_VERIFY_THROTTLED: 'CONTACT_VERIFY_THROTTLED',
  /// Bu kanal kodla değil bağlantıyla doğrulanıyor (Telegram) — `PUT
  /// /contacts` ve `/contacts/verify` reddediyor, `POST /contacts/telegram`
  /// kullanılmalı.
  CONTACT_LINK_REQUIRED: 'CONTACT_LINK_REQUIRED',
} as const;
export type NotificationErrorCode =
  (typeof NotificationErrorCode)[keyof typeof NotificationErrorCode];
